"use client";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardHeader,
  CardContent,
} from "@/components/ui/card";
import AuthService from "@/lib/auth.ts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { validateNPI, formatPhoneDash, handlePhoneInputChange, handlePhoneKeyDown } from "@/lib/utils";
import { 
  User, 
  Shield, 
  BadgeCheck, 
  Stethoscope, 
  Save, 
  Loader2 
} from "lucide-react";

const staffSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email"),
  role: z.string().min(1, "Role is required"),
  auth_uuid: z.string().optional(),
  location_ids: z.any().optional(),
  npi: z.string().optional().refine(validateNPI, {
    message: "Invalid NPI: Must be 10 digits starting with 1 or 2, and have a valid checksum",
  }),
  phone_number: z.string().optional().refine(
    (v) => {
      if (!v) return true;
      const digits = v.replace(/\D/g, "");
      return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
    },
    "Phone number must be exactly 10 digits",
  ),
  primary_doctor_associated: z.string().optional(),
  secondary_doctor_associated: z.string().optional(),
});

type StaffFormValues = z.infer<typeof staffSchema>;

interface StaffUpdateFormProps {
  staffData: any;
  onSuccess?: () => void;
}

export default function StaffUpdateForm({ staffData, onSuccess }: StaffUpdateFormProps) {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<string[]>([]);
  const [rolesMetadata, setRolesMetadata] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectModal, setConnectModal] = useState<{
    name: string; username: string; tempPassword: string;
  } | null>(null);
  const [doctorsList, setDoctorsList] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  const form = useForm<StaffFormValues>({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      id: staffData?.id || "",
      name: staffData?.name || staffData?.full_name || "",
      email: staffData?.email || "",
      role: staffData?.role || "",
      auth_uuid: staffData?.auth_uuid || "",
      npi: staffData?.npi || "",
      phone_number: formatPhoneDash(staffData?.phone_number || ""),
      primary_doctor_associated: staffData?.primary_doctor_associated || "",
      secondary_doctor_associated: staffData?.secondary_doctor_associated || "",
      location_ids: staffData?.location_ids || "",
    },
  });

  const watchedRole = form.watch("role");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        const rolesRes = await AuthService.getRolesMetadata();

        // Fetch all roles with is_allowed_to_add_notes=true (Doctor, NP, PA, CareCoordinator, etc.)
        const cpRoleNames = rolesRes
          .filter((r: any) => r.is_allowed_to_add_notes === true)
          .map((r: any) => r.name || r.role);

        const cpResults = await Promise.all(
          cpRoleNames.map((role: string) => AuthService.getStaffByRole(role))
        );
        const allCpStaff = cpResults.flat();
        // Deduplicate by id
        const uniqueCpStaff = Array.from(new Map(allCpStaff.map((s: any) => [s.id, s])).values());

        setDoctorsList(uniqueCpStaff);
        setRolesMetadata(rolesRes);
        
        // Include all roles in assignment options
        const filteredRoles = rolesRes
          .map((r: any) => r.role)
          .filter(Boolean)
          .sort();
          
        setRoles(filteredRoles);
      } catch (err) {
        console.error("Error fetching support data:", err);
      } finally {
        setDataLoading(false);
        setRolesLoading(false);
      }
    };
    fetchData();
  }, []);

  const onSubmit = async (values: StaffFormValues) => {
    try {
      setIsSubmitting(true);
      
      // Find role_id from metadata if possible
      const roleObj = rolesMetadata.find(r => 
        (r.role || r.name || "").toLowerCase() === (values.role || "").toLowerCase()
      );
      
      const cleanPhoneDigits = values.phone_number
        ? values.phone_number.replace(/\D/g, "").replace(/^1/, "").slice(-10)
        : "";
      const updateData = {
        ...values,
        phone_number: cleanPhoneDigits ? `+1${cleanPhoneDigits}` : null,
        full_name: values.name, // Ensure consistency with DB field
        role_id: roleObj?.id || roleObj?.role_id,
      };

      console.log("🚀 [StaffUpdateForm] Submitting update payload:", JSON.stringify(updateData, null, 2));
      console.log("🔍 [StaffUpdateForm] Selected Role Metadata:", roleObj);

      const response = await AuthService.updateStaff(values.id!, updateData);
      
      if (response.error) {
        throw new Error(response.error.message || "Failed to update staff");
      }

      const connect = response.connect_provisioning;
      const roleChanged = values.role !== staffData?.role;

      if (connect?.provisioned) {
        // Role promoted to clinical AND Connect account created successfully
        toast.success(
          `${values.name}'s profile updated and Amazon Connect account created successfully.`
        );
        if (connect.already_existed) {
          toast.info(
            `${values.name} already had a Connect account. They must log out and log back in to access the phone widget.`,
            { duration: 8000 }
          );
        } else {
          // Show a persistent modal — a toast disappears and the admin may miss
          // the temp password, leaving the user unable to log into Connect.
          setConnectModal({
            name:         values.name,
            username:     connect.username || values.email,
            tempPassword: connect.temp_password,
          });
        }
      } else if (connect && !connect.provisioned) {
        // Role promoted to clinical BUT Connect account creation failed
        toast.success(`${values.name}'s profile updated successfully.`);
        toast.warning(
          `Amazon Connect account could not be created: ${connect.error ?? "Unknown error"}. ` +
          `${values.name} will not be able to use the softphone until this is resolved. ` +
          `Please contact your system administrator.`,
          { duration: 12000 }
        );
      } else {
        // Normal update — no clinical promotion involved
        toast.success("Staff profile updated successfully!");
        if (roleChanged) {
          toast.info(
            `${values.name} must log out and log back in for the role change to take effect.`,
            { duration: 6000 }
          );
        }
      }

      // When the Connect modal is shown, do NOT navigate away — the modal would be
      // destroyed immediately since navigate unmounts the component.
      // Navigation happens inside the modal's Close button handler instead.
      if (!connect?.provisioned || connect?.already_existed) {
        if (onSuccess) onSuccess();
        navigate("/admin/staff");
      }
    } catch (err: any) {
      console.error("Error updating staff:", err);
      toast.error(err.message || "An error occurred during update");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isClinical = (roleName: string) => {
    return rolesMetadata.find(r => r.role.toLowerCase() === (roleName || "").toLowerCase())?.is_clinical || false;
  };

  // is_standalone_cp=TRUE means Doctor / CareCoordinator — supervision anchors.
  // These roles must NOT show primary/secondary CP association fields.
  const isStandalone = (roleName: string) => {
    return rolesMetadata.find(r => r.role.toLowerCase() === (roleName || "").toLowerCase())?.is_standalone_cp || false;
  };

  // Only Doctor and CareCoordinator (is_standalone_cp=TRUE) can supervise other clinical staff.
  // NP, PA, and other supervised roles must NOT appear in the Primary/Secondary Doctor dropdowns
  // — an NP cannot supervise another NP or PA and vice versa.
  const supervisingCpList = doctorsList.filter(doc => {
    const meta = rolesMetadata.find(
      r => (r.role || r.name || "").toLowerCase() === (doc.role || "").toLowerCase()
    );
    return meta?.is_standalone_cp === true;
  });

  return (
    <>
    <Card className="rounded-3xl border-gray-100 shadow-xl bg-white overflow-hidden">
      <CardHeader className="bg-gray-50/50 border-b border-gray-100 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#1a7a4a] flex items-center justify-center text-white shadow-lg">
            <User className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Staff Profile</h2>
            <p className="text-sm text-gray-500 font-medium">Update account details and role permissions</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            
            {/* Section: Personal Information */}
            <div className="space-y-6">
              <h3 className="text-sm font-black text-[#1a7a4a] uppercase tracking-widest flex items-center gap-2">
                <BadgeCheck className="h-4 w-4" /> Personal Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="email@example.com" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Section: Role & Branch */}
            <div className="space-y-6">
              <h3 className="text-sm font-black text-[#1a7a4a] uppercase tracking-widest flex items-center gap-2">
                <Shield className="h-4 w-4" /> Role & Assignment
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>System Role</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value || undefined}
                        disabled={rolesLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={rolesLoading ? "Loading roles..." : "Select Role"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {roles.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Section: Clinical Information (Conditional) */}
            {isClinical(watchedRole) && (
              <div className="space-y-8 pt-4">
                <div className="space-y-6">
                  <h3 className="text-sm font-black text-[#1a7a4a] uppercase tracking-widest flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" /> Clinical Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="npi"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>NPI Number</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter NPI" 
                              {...field} 
                              maxLength={10}
                              onInput={(e) => {
                                const target = e.target as HTMLInputElement;
                                target.value = target.value.replace(/\D/g, "");
                                field.onChange(target.value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number <span className="text-gray-400 font-normal">(optional)</span></FormLabel>
                          <FormControl>
                            <div className="relative flex items-center">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm pointer-events-none select-none">+1</span>
                              <Input
                                placeholder="###-###-####"
                                maxLength={12}
                                className="pl-9"
                                value={formatPhoneDash(field.value || "")}
                                onChange={(e) => {
                                  handlePhoneInputChange(e, (formatted) => {
                                    field.onChange(formatted);
                                  }, formatPhoneDash);
                                }}
                                onKeyDown={handlePhoneKeyDown}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Doctor Associations — only for supervised clinical roles (is_standalone_cp=FALSE).
                    Standalone anchors (Doctor, CareCoordinator) must NOT have these fields. */}
                {!isStandalone(watchedRole) && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-black text-[#1a7a4a] uppercase tracking-widest flex items-center gap-2">
                      <User className="h-4 w-4" /> Doctor Associations
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="primary_doctor_associated"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Primary Doctor</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value || undefined}
                              disabled={dataLoading}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={dataLoading ? "Loading..." : "Select Primary Doctor"} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {/* Fallback: inject saved primary CP as an option if doctorsList
                                    hasn't loaded yet or doesn't contain the saved UUID. Prevents
                                    Radix Select from showing blank when value is set but no
                                    matching SelectItem exists. Uses UUID as label since
                                    StaffUpdateForm has no enriched name in props. */}
                                {field.value && !supervisingCpList.find(d => d.id === field.value) && (
                                  <SelectItem key={field.value} value={field.value}>
                                    {staffData?.primary_doctor_name || field.value}
                                  </SelectItem>
                                )}
                                {supervisingCpList.map(doc => (
                                  <SelectItem key={doc.id} value={doc.id}>{doc.name || doc.full_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="secondary_doctor_associated"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Secondary Doctor</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value || undefined}
                              disabled={dataLoading}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={dataLoading ? "Loading..." : "Select Secondary Doctor"} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {/* Fallback: inject saved secondary CP as an option if doctorsList
                                    hasn't loaded yet or doesn't contain the saved UUID. Same
                                    timing race fix as primary CP above. */}
                                {field.value && !supervisingCpList.find(d => d.id === field.value) && (
                                  <SelectItem key={field.value} value={field.value}>
                                    {staffData?.secondary_doctor_name || field.value}
                                  </SelectItem>
                                )}
                                {supervisingCpList.map(doc => (
                                  <SelectItem key={doc.id} value={doc.id}>{doc.name || doc.full_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/admin/staff")}
                className="rounded-2xl px-6 h-12 font-bold text-gray-500"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !form.formState.isDirty}
                className="bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 px-8 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-bold"
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Save className="h-5 w-5" />
                )}
                Save Staff Changes
              </Button>
            </div>

          </form>
        </Form>
      </CardContent>
    </Card>

    {/* ── Connect temp-password modal ───────────────────────────────────────
        Shown when a clinical role is assigned and a NEW Connect account is
        created. Requires explicit dismissal so the BranchAdmin cannot miss
        the temp password. Uses only existing Tailwind classes — no new deps. */}
    {connectModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 space-y-5">

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#1a7a4a]/10 flex items-center justify-center">
              <span className="text-xl">📞</span>
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-gray-900">Amazon Connect Account Created</h2>
              <p className="text-xs text-gray-400">Share these credentials with {connectModal.name} securely</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-1">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Connect Username</p>
              <p className="text-sm font-mono font-semibold text-gray-800 break-all">{connectModal.username}</p>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-2">
              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Temporary Password</p>
              <div className="flex items-center justify-between gap-3">
                <p className="text-base font-mono font-bold text-amber-800 tracking-widest break-all">
                  {connectModal.tempPassword}
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(connectModal.tempPassword);
                    toast.success("Password copied to clipboard");
                  }}
                  className="flex-shrink-0 text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg px-3 py-1.5 transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-blue-50 border border-blue-100 p-3">
            <p className="text-xs text-blue-700 leading-relaxed">
              <strong>Instructions for {connectModal.name}:</strong><br/>
              1. Log into the application with their email and app password.<br/>
              2. Click the phone icon in the sidebar.<br/>
              3. Click <em>"Login to Connect Phone"</em> and enter the credentials above.<br/>
              4. They will be prompted to set a new Connect password on first login.
            </p>
          </div>

          <button
            onClick={() => {
              setConnectModal(null);
              if (onSuccess) onSuccess();
              navigate("/admin/staff");
            }}
            className="w-full h-11 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold text-sm transition-all hover:scale-[1.01]"
          >
            I've noted the credentials — Close
          </button>

        </div>
      </div>
    )}
    </>
  );
}
